import React, { useEffect, useRef, useState } from "react";
import { Loader2, UserRound } from "lucide-react";

/* Live AI interviewer — a 3D avatar (met4citizen/TalkingHead, MIT) rendered in
   the browser, free, no per-minute cost. Vapi keeps doing the thinking and the
   voice; this gives that voice a face. Lip movement is driven in real time from
   Vapi's `volume-level` events (assistant speech energy), so the mouth moves
   exactly while the interviewer speaks — no extra audio, no server.

   Avatar: any Ready Player Me GLB with ARKit + Oculus viseme morph targets.
   Make your own at readyplayer.me and set REACT_APP_INTERVIEWER_AVATAR_URL. */

// Self-hosted (public/avatars) so it works offline and behind college firewalls.
// To change the interviewer: export a GLB from readyplayer.me with
// ?morphTargets=ARKit,Oculus+Visemes,mouthOpen,mouthSmile,eyesClosed,eyesLookUp,eyesLookDown
// into public/avatars and point REACT_APP_INTERVIEWER_AVATAR_URL at it.
const DEFAULT_AVATAR = process.env.REACT_APP_INTERVIEWER_AVATAR_URL || "/avatars/interviewer-1.glb";

// Mouth shapes we drive from speech energy. Mixed by phase so the mouth doesn't
// just open and close like a puppet.
const MOUTH = ["jawOpen", "viseme_aa", "viseme_O", "viseme_E", "mouthFunnel"];

export default function AvatarInterviewer({ vapiRef, status, speaking, mood = "neutral", className = "" }) {
  const boxRef = useRef(null);
  const headRef = useRef(null);
  const volRef = useRef(0);
  const rafRef = useRef(null);
  const [state, setState] = useState("loading"); // loading | ready | error
  const [err, setErr] = useState("");

  // Mount the avatar once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { TalkingHead } = await import("@met4citizen/talkinghead");
        if (cancelled || !boxRef.current) return;
        const head = new TalkingHead(boxRef.current, {
          ttsEndpoint: null,
          lipsyncModules: [],          // we drive the mouth ourselves (bundler-safe)
          cameraView: "upper",
          cameraRotateEnable: false,
          cameraPanEnable: false,
          cameraZoomEnable: false,
          avatarMood: mood,
          modelFPS: 30,
          lightAmbientIntensity: 2.2,
          lightDirectIntensity: 24,
        });
        headRef.current = head;
        await head.showAvatar({ url: DEFAULT_AVATAR, body: "F", avatarMood: mood, avatarIdleEyeContact: 0.6, avatarListeningEyeContact: 0.9, avatarSpeakingEyeContact: 0.8 });
        if (cancelled) return;
        setState("ready");
        // Lip-sync loop: smooth the last volume reading into mouth morph targets.
        let level = 0, t = 0;
        const tick = () => {
          const target = Math.min(1, volRef.current * 1.8);
          level += (target - level) * (target > level ? 0.45 : 0.25);
          t += 1;
          const mt = head.mtAvatar || {};
          const shapes = {
            jawOpen: level * 0.55,
            viseme_aa: level * (0.6 + 0.3 * Math.sin(t / 3)),
            viseme_O: level * (0.35 + 0.35 * Math.sin(t / 5 + 1)),
            viseme_E: level * (0.25 + 0.25 * Math.cos(t / 4)),
            mouthFunnel: level * 0.15,
          };
          for (const k of MOUTH) {
            if (mt[k]) Object.assign(mt[k], { realtime: level < 0.02 ? 0 : shapes[k], needsUpdate: true });
          }
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        if (process.env.NODE_ENV !== "production") window.__avatarVolume = (v) => { volRef.current = v; };
      } catch (e) {
        console.error("Avatar failed to load", e);
        if (!cancelled) { setErr(e?.message || "Avatar unavailable"); setState("error"); }
      }
    })();
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      try { headRef.current?.stop?.(); headRef.current?.dispose?.(); } catch (_) {}
      headRef.current = null;
    };
  }, []); // eslint-disable-line

  // Subscribe to Vapi's assistant volume once the call object exists.
  useEffect(() => {
    const v = vapiRef?.current;
    if (!v || typeof v.on !== "function") return;
    const onVol = (level) => { volRef.current = typeof level === "number" ? level : 0; };
    v.on("volume-level", onVol);
    return () => { try { v.off?.("volume-level", onVol); } catch (_) {} };
  }, [vapiRef?.current, status]); // eslint-disable-line

  // Reactions: look at the camera when speaking; mood on finish.
  useEffect(() => {
    const head = headRef.current;
    if (!head || state !== "ready") return;
    try {
      if (speaking) head.lookAtCamera?.(4000);
      else volRef.current = 0;
      if (status === "done") head.setMood?.("happy");
      else if (status === "live") head.setMood?.(mood);
    } catch (_) {}
  }, [speaking, status, state, mood]);

  return (
    <div className={`relative overflow-hidden rounded-2xl bg-[#2a2522] ${className}`} data-testid="avatar-interviewer">
      <div ref={boxRef} className="absolute inset-0" />
      {state === "loading" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-stone-400 text-xs">
          <Loader2 size={18} className="animate-spin text-amber-300" /> Loading your interviewer…
        </div>
      )}
      {state === "error" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-stone-400 text-xs px-6 text-center">
          <UserRound size={22} /> {err || "Interviewer avatar unavailable"} — the voice interview still works.
        </div>
      )}
      {state === "ready" && (
        <div className="absolute left-3 bottom-3 flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${speaking ? "bg-amber-300 wsh-pulse" : status === "live" ? "bg-emerald-400" : "bg-stone-500"}`} />
          <span className="text-[10px] font-mono uppercase tracking-widest text-stone-300">{speaking ? "Interviewer speaking" : status === "live" ? "Listening to you" : "Interviewer"}</span>
        </div>
      )}
    </div>
  );
}
